import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { Dispatch } from 'redux';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import { LegalPoliceDomesticViolenceItemModel } from 'forms/sections/legal/police/domesticViolence/types';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import Location from 'components/Form/Location';
import PVQLocation from 'components/Form/PVQLocation/Location';
import Text from 'components/Form/Text';
import { isFormSF852013, isPVQForm } from 'helpers/form';
import Dropdown from 'components/Form/Dropdown';
import { domesticOrderOptions } from 'constants/enums/legalOptions';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import { CheckboxModel } from 'forms/shared/types';

interface Props extends Partial<LegalPoliceDomesticViolenceItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
}

export default class DomesticViolence extends ValidationElement<Props> {
  update = (queue: Partial<LegalPoliceDomesticViolenceItemModel>) => {
    this.props.onUpdate({
      Explanation: this.props.Explanation,
      Issued: this.props.Issued,
      CourtName: this.props.CourtName,
      CourtAddress: this.props.CourtAddress,
      ProtectedParty: this.props.ProtectedParty,
      OrderExpiration: this.props.OrderExpiration,
      OrderIssuedReason: this.props.OrderIssuedReason,
      ...queue,
    });
  };

  updateDate = (value: LegalPoliceDomesticViolenceItemModel['Issued']) => {
    this.update({
      Issued: value,
    });
  };

  updateExplanation = (value: LegalPoliceDomesticViolenceItemModel['Explanation']) => {
    this.update({
      Explanation: value,
    });
  };

  updateCourtName = (value: LegalPoliceDomesticViolenceItemModel['CourtName']) => {
    this.update({
      CourtName: value,
    });
  };

  updateCourtAddress = (value: LegalPoliceDomesticViolenceItemModel['CourtAddress']) => {
    this.update({
      CourtAddress: value,
    });
  };

  updateIssued = (value: LegalPoliceDomesticViolenceItemModel['Issued']) => {
    this.update({
      Issued: value,
    });
  };

  updateProtectedParty = (values: LegalPoliceDomesticViolenceItemModel['ProtectedParty']) => {
    const protectedParty = values?.value;
    const selected = [...((this.props.ProtectedParty || {}).values || [])];
    if (selected.includes(protectedParty)) {
      selected.splice(selected.indexOf(protectedParty), 1);
    } else {
      selected.push(protectedParty);
    }
    this.update({
      ProtectedParty: { values: selected },
    });
  };

  updateOrderExpiration = (value: LegalPoliceDomesticViolenceItemModel['OrderExpiration']) => {
    this.update({
      OrderExpiration: value,
    });
  };

  updateOrderIssuedReason = (value: LegalPoliceDomesticViolenceItemModel['OrderIssuedReason']) => {
    this.update({
      OrderIssuedReason: value,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const { formType } = this.props;
    const isPVQ = isPVQForm(formType);

    return isPVQ ? (
      <div className="domestic-violence">
        <Field
          title={i18n.t('legal.pvq.police.heading.domesticCourtDate')}
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Issued`}
        >
          <DateControl
            {...this.props.Issued}
            name="Issued"
            hideDay
            className="issued"
            minDateEqualTo
            maxDate={{ year: new Date().getFullYear() }}
            onUpdate={this.updateIssued}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Issued`}
          />
        </Field>

        <PVQLocation
          {...this.props.CourtAddress}
          name="CourtAddress"
          attribute={`${attribute}.CourtAddress`}
          label={i18n.t('legal.pvq.police.heading.domesticCourtAddress')}
          domesticTitle={i18n.t('legal.pvq.police.label.courtAddressUS')}
          foreignTitle={i18n.t('legal.pvq.police.label.courtAddressForeign')}
          layout={Location.ADDRESS}
          geocode
          addressBook="Facility"
          addressBooks={this.props.addressBooks}
          dispatch={this.props.dispatch}
          onUpdate={this.updateCourtAddress}
          onError={this.props.onError}
          required={this.props.required}
          isPoBoxAllowed={false}
          showInternationalStreet={false}
        />

        <Field
          attribute={`${attribute}.ProtectedParty`}
          title={i18n.t('legal.pvq.police.heading.protectedparty')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
        >
          {/* (Select all if more than one party.) */}
          <p>{i18n.t('legal.pvq.police.label.domesticViolenceSelectAll')}</p>
          <CheckboxGroup
            className="protected-pary option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValues={(this.props.ProtectedParty || {}).values}
            attribute={`${attribute}.ProtectedParty`}
          >
            {domesticOrderOptions.map((s) => (
              <Checkbox
                name="protected-party"
                label={s}
                value={s}
                className="legal-police-domestic-protected-party-checkbox"
                onUpdate={this.updateProtectedParty}
                onError={this.props.onError}
              />
            ))}
          </CheckboxGroup>
        </Field>
        {this.props.ProtectedParty?.values?.includes('Other') && (
          <Field
            title={i18n.t('legal.pvq.police.heading.domesticExplanation')}
            help="legal.pvq.police.help.domesticExplanation"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Explanation`}
          >
            <Textarea
              {...this.props.Explanation}
              className="explanation"
              name="explanation"
              onUpdate={this.updateExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Explanation`}
            />
          </Field>
        )}
        <Field
          title={i18n.t('legal.pvq.police.heading.domesticOrderExpirationDate')}
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.OrderExpiration`}
        >
          <DateControl
            {...this.props.OrderExpiration}
            name="OrderExpiration"
            hideDay
            className="issued"
            minDateEqualTo
            onUpdate={this.updateOrderExpiration}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.OrderExpiration`}
          />
        </Field>
        <Field
          title={i18n.t('legal.pvq.police.heading.domesticOrderIssuedReason')}
          help="legal.pvq.police.help.domesticOrderIssuedReason"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.domesticOrderIssuedReason`}
        >
          <Textarea
            {...this.props.OrderIssuedReason}
            className="explanation"
            name="orderIssuedReason"
            onUpdate={this.updateOrderIssuedReason}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.OrderIssuedReason`}
          />
        </Field>
      </div>
    )
      : (
        <div className="domestic-violence">
          <Field
            title={i18n.t('legal.police.heading.domesticExplanation')}
            help="legal.police.help.domesticExplanation"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Explanation`}
          >
            <Textarea
              className="explanation"
              name="explanation"
              {...this.props.Explanation}
              onUpdate={this.updateExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Explanation`}
            />
          </Field>

          <Field
            title={i18n.t('legal.police.heading.domesticCourtDate')}
            adjustFor="labels"
            shrink
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Issued`}
          >
            <DateControl
              name="Issued"
              {...this.props.Issued}
              hideDay
              className="issued"
              minDateEqualTo
              maxDate={{ year: new Date().getFullYear() }}
              onUpdate={this.updateIssued}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Issued`}
            />
          </Field>

          <Field
            title={i18n.t('legal.police.heading.domesticCourtName')}
            adjustFor="labels"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.CourtName`}
          >
            <Text
              name="CourtName"
              {...this.props.CourtName}
              className="domestic-courtname"
              onUpdate={this.updateCourtName}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.CourtName`}
            />
          </Field>

          <Field
            title={i18n.t('legal.police.heading.domesticCourtAddress')}
            optional
            help="legal.police.help.courtaddress"
            adjustFor="address"
            shrink
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.CourtAddress`}
          >
            <Location
              name="CourtAddress"
              {...this.props.CourtAddress}
              label={i18n.t('legal.police.label.address')}
              className="domestic-courtaddress"
              layout={Location.OFFENSE}
              geocode
              addressBooks={this.props.addressBooks}
              addressBook="Court"
              dispatch={this.props.dispatch}
              onUpdate={this.updateCourtAddress}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.CourtAddress`}
            />
          </Field>
        </div>
      );
  }
}

DomesticViolence.defaultProps = {
  addressBooks: {},
  ProtectedParty: {},
  OrderExpiration: {},
  OrderIssuedReason: {},
  dispatch: (action) => { },
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode[]) => arr,
};
